/**
 * Drafts (sites with a preview branch): the bar with unpublished changes on every site screen, the publish and
 * discard confirmations, and the preview link.
 */
import { useState, type ReactNode } from 'react';
import type { PendingChanges } from '@hotpot/core';
import { useT } from '../i18n.js';
import { joinUrl, relativeTime } from '../lib/format.js';
import { Link } from '../router.js';
import { useMaybeSite, useToast } from '../state.js';
import { errorText } from '../screens/Login.js';
import { routeForPath } from '../screens/SiteHome.js';
import { Dialog, Status, useConfirm } from './ui.js';

/** "Preview ↗" for a path on the preview site, or the preview build's state while it builds. */
export function PreviewLink({ path = '', className = 'link-btn' }: { path?: string; className?: string }) {
  const { t } = useT();
  const site = useMaybeSite();
  const toast = useToast();
  const drafts = site?.handle.drafts;
  if (!site || !drafts) return null;
  const url = drafts.previewUrl ? joinUrl(drafts.previewUrl, path) : '';
  const building = site.preview && site.preview.status.state !== 'live' ? site.preview : null;
  if (building) return <Status status={building.status} small preview />;
  if (!url) {
    return (
      <button className={className} onClick={() => toast(t('previewDemo'))}>
        {t('preview')} ↗
      </button>
    );
  }
  return (
    <a className={className} href={url} target="_blank" rel="noopener noreferrer">
      {t('preview')} ↗
    </a>
  );
}

/** The list of unpublished changes (commit messages, who and when, linked to the entries). */
function PendingList({ pending }: { pending: PendingChanges }) {
  const { t, lang } = useT();
  const site = useMaybeSite()!;
  return (
    <ul className="pending-list">
      {pending.commits.map((c) => {
        const links = (c.files ?? []).map((p) => routeForPath(site.config, site.handle.site.id, p)).filter((x): x is string => !!x);
        return (
          <li key={c.sha}>
            {links.length === 1 ? <Link to={links[0]!}>{c.message}</Link> : c.message}
            <span className="muted small">
              {' '}
              · {c.author.name} · {relativeTime(c.date, t, lang)}
            </span>
          </li>
        );
      })}
      {!pending.commits.length && pending.files.map((f) => <li key={f.path}>{f.path}</li>)}
    </ul>
  );
}

export function useDraftActions() {
  const { t } = useT();
  const site = useMaybeSite();
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [ask, setAsk] = useState<null | ((ok: boolean) => void)>(null);
  const [busy, setBusy] = useState(false);
  const n = site?.pending?.files.length ?? 0;

  const publish = async (opts: { confirm?: boolean; url?: string } = {}) => {
    if (!site) return false;
    if (opts.confirm !== false) {
      const ok = await new Promise<boolean>((resolve) => setAsk(() => resolve));
      setAsk(null);
      if (!ok) return false;
    }
    setBusy(true);
    try {
      const done = await site.publishAll(opts.url);
      toast(done ? t('published') : t('nothingToPublish'));
      return done;
    } catch (e) {
      toast(errorText(e, t));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!site) return;
    if (!(await confirm({ title: t('discardTitle'), text: t('discardText', { n }), ok: t('discardAll') }))) return;
    setBusy(true);
    try {
      await site.discardAll();
      toast(t('discarded'));
    } catch (e) {
      toast(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const dialogs: ReactNode = (
    <>
      {confirmDialog}
      {ask && site?.pending && (
        <Dialog
          title={t('publishTitle')}
          onClose={() => ask(false)}
          wide
          buttons={
            <>
              <button className="link-btn" onClick={() => ask(false)}>
                {t('cancel')}
              </button>
              <button className="btn" onClick={() => ask(true)}>
                {t('publishAll')}
              </button>
            </>
          }
        >
          <p style={{ margin: '0 0 12px' }}>{t('publishText', { site: site.handle.site.name })}</p>
          <PendingList pending={site.pending} />
        </Dialog>
      )}
    </>
  );
  return { publish, discard, busy, dialogs };
}

/** Shown at the top of every site screen while there are unpublished changes. */
export function DraftsBar() {
  const { t } = useT();
  const site = useMaybeSite();
  const [show, setShow] = useState(false);
  const { publish, discard, busy, dialogs } = useDraftActions();
  const n = site?.pending?.files.length ?? 0;
  if (!site?.handle.drafts || !n) return dialogs;
  return (
    <>
      <div className="drafts-bar" role="status">
        <span className="count">
          <span className="dot" aria-hidden="true" />
          {t('pendingCount', { n })}
        </span>
        <PreviewLink />
        <button className="link-btn" onClick={() => setShow(true)}>
          {t('pendingShow')}
        </button>
        <button className="link-btn" onClick={discard} disabled={busy}>
          {t('discardAll')}
        </button>
        <button className="link-btn ink strong" onClick={() => publish()} disabled={busy}>
          {busy ? t('publishPending') : `${t('publishAll')} →`}
        </button>
      </div>
      {show && site.pending && (
        <Dialog
          title={t('pendingCount', { n })}
          onClose={() => setShow(false)}
          wide
          buttons={
            <button className="btn" onClick={() => setShow(false)}>
              {t('close')}
            </button>
          }
        >
          <p style={{ margin: '0 0 12px' }}>{t('pendingIntro')}</p>
          <PendingList pending={site.pending} />
        </Dialog>
      )}
      {dialogs}
    </>
  );
}
